import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as Papa from 'papaparse';
import * as JSZip from 'jszip';
import { saveAs } from 'file-saver';

import { corsProxy } from '../../../environments/environment';
import { HelperService as Helper } from '../../services/helper/helper.service';
import { ProductService } from '../../services/product/product.service';
import { EditedImagesService } from '../../services/edited-images/edited-images.service';
import { AnyMap, StringMap } from '../../types/basics';


type ImageMap = StringMap;

type ImageMapInFolders = {
	[ key : string ] : ImageMap;
};



@Injectable({
	providedIn : 'root',
})
export class DownloadService {
	loading : boolean = false;
	query$  : BehaviorSubject<string> = new BehaviorSubject<string>('');


	constructor(
		private product      : ProductService,
		private editedImages : EditedImagesService,
	) {}


	private toggleLoading(forceState? : boolean) : void {
		this.loading = forceState == null ? !this.loading : forceState;
	}

	async zipOfImages(imagesOrFolders : ImageMap | ImageMapInFolders, zipFilename : string) : Promise<void> {
		const zip : JSZip = new JSZip();

		const entries = Object.entries(imagesOrFolders);

		if (typeof entries?.[0]?.[1] === 'string') {
			this.zipImages(imagesOrFolders as ImageMap, zip);
		} else {
			for (const [ folderName, images ] of entries) {
				this.zipImages(images as ImageMap, zip, folderName);
			}
		}

		const blob : Blob = await zip.generateAsync({ type : 'blob' });

		return this.saveFile(blob, zipFilename, 'zip');
	}

	private zipImages(images : ImageMap, zip : JSZip, folderName? : string) : void {
		for (const entry of Object.entries(images)) {
			const source = entry[1];
			const fileExtension = Helper.parsePath(source).fileExtension ?? '.jpg';

			let filename = entry[0] + fileExtension;

			if (folderName) {
				filename = `${ folderName }/${ filename }`;
			}

			zip.file(filename, this.getBlobPromise(source), { binary : true });
		}
	}

	private getBlobPromise(url : string) : Promise<Blob> {
		return fetch(`https://${ corsProxy.proxyDomain }/${ url }`, {
			headers : {
				[corsProxy.proxyRequestHeader] : 'true',
			},
		})
			.then((response) => response.blob());
	}

	saveFile(file : string | Blob, filename : string = 'download', filetype : string = 'txt') : void {
		if (!Helper.parsePath(filename).fileExtension) {
			// in case the passed filetype is prefixed with a period
			// such as with HelperClass.parseUrl(…).fileExtension
			// remove it
			filetype = filetype.replace(/^\./, '');

			filename = `${ filename }.${ filetype }`;
		}

		return saveAs(file, filename);
	}

	async zipOfCsv(data : any = {}, csvFilename : string = 'export', zipFilename : string = 'export') : Promise<void> {
		const zip     : JSZip = new JSZip();
		const csvBlob : Blob = this.getCsvBlob(data);

		zip.file(`${ csvFilename }.csv`, csvBlob, { binary : true });

		const zipBlob = await zip.generateAsync({ type : 'blob' });

		return this.saveFile(zipBlob, zipFilename, 'zip');
	}

	csv(data : AnyMap, filename? : string) : void {
		return this.csvOrJson(data, 'csv', filename);
	}

	json(data : string | AnyMap, filename? : string) : void {
		return this.csvOrJson(data, 'json', filename);
	}

	private csvOrJson(data : string | AnyMap, filetype : 'csv' | 'json', filename : string = 'export') : void {
		const blob = filetype === 'csv' ? this.getCsvBlob(data) : this.getJsonBlob(data);

		return this.saveFile(blob, filename, filetype);
	}

	private getCsvBlob(data : any = {}) : Blob {
		return this.getBlob(Papa.unparse(data), 'text/csv;charset=utf-8;');
	}

	private getJsonBlob(data : string | AnyMap = '') : Blob {
		return this.getBlob(
			typeof data === 'string' ? data : JSON.stringify(data, null, 2),
			'application/json',
		);
	}

	private getBlob(data : any, type : string) : Blob {
		return new Blob([ data ], { type });
	}
}
